/**
 * Менеджер состояния (Pub/Sub pattern)
 * Управляет глобальным состоянием приложения и уведомляет подписчиков об изменениях.
 */

import type { AppState, StateAction } from './types';
//import type { FiltersState } from './filters/types';

class StateManager {
  private state: AppState = {
    currentView: 'map',

    parametersDict: [],
    parametersDictLoading: false,

    labsDict: [],
    labsDictLoading: false,
        
    expeditionsList: [],
    selectedExpedition: null,
    expeditionsLoading: false,
    expeditionsError: null,

    vesselsList: [],
    selectedVessel: null,
    vesselsLoading: false,
    vesselsError: null,

    selectedSite: null,
    
    siteTimeSeries: null,
    siteTimeSeriesLoading: false,
    selectedSiteParameter: null,

    selectedTraverse: null,
        
    selectedObservationId: null,
    selectedObservation: null,
    insituSamples: [],
    labSamples: [],
    activeTab: 'info',
    isLoading: false,
    error: null,
    isSidebarOpen: false,

    publicationsList: [],
    publicationsLoading: false,
    publicationsError: null,
    
    filters: {
      values: {},
      options: {},
      loading: {}
    },
  
    pagesList: [],
    pagesLoading: false,
    pagesError: null,
    selectedPage: null,
    pageContent: null,
    pageLoading: false,
    pageError: null,
  };

  private listeners = new Set<(state: AppState) => void>();
  private isNotifying = false; // Флаг защиты от бесконечной рекурсии

  /**
   * Получить текущее состояние (копия для иммутабельности)
   */
  getState(): AppState {
    return { ...this.state };
  }

  /**
   * Применить действие и уведомить подписчиков
   */
  dispatch(action: StateAction): void {
    if (action.type === 'SET_ERROR' && this.state.error === action.payload) return;
    if (action.type === 'SET_EXPEDITIONS_ERROR' && this.state.expeditionsError === action.payload) return;
    if (action.type === 'SET_VESSELS_ERROR' && this.state.vesselsError === action.payload) return;

    switch (action.type) {
      case 'SET_VIEW':
        console.log('SET_VIEW dispatched with ', action.payload);
        this.state.currentView = action.payload;
        if (action.payload !== 'map') {
          this.state.selectedObservationId = null;
          this.state.selectedObservation = null;
          this.state.insituSamples = [];
          this.state.labSamples = [];
          this.state.isSidebarOpen = false;
        }
        // При переходе на другую вкладку сбрасываем выбор экспедиции (опционально)
//        if (action.payload !== 'expeditions') { this.state.selectedExpedition = null; }
//        if (action.payload !== 'fleet') this.state.selectedVessel = null; // НОВОЕ
        
        break;

      case 'SET_PUBLICATIONS_LIST':
        this.state.publicationsList = action.payload;
        this.state.publicationsLoading = false;
        this.state.publicationsError = null;
        break;

      case 'SET_PUBLICATIONS_LOADING':
        this.state.publicationsLoading = action.payload;
        if (action.payload) this.state.publicationsError = null;
        break;

      case 'SET_PUBLICATIONS_ERROR':
        this.state.publicationsError = action.payload;
        this.state.publicationsLoading = false;
        break;

      case 'SET_PARAMETERS_DICT':
        this.state.parametersDict = action.payload;
        this.state.parametersDictLoading = false;
        break;

      case 'SET_PARAMETERS_DICT_LOADING':
        this.state.parametersDictLoading = action.payload;
        break;

      case 'SET_LABS_DICT':
        this.state.labsDict = action.payload;
        this.state.labsDictLoading = false;
        break;

      case 'SET_LABS_DICT_LOADING':
        this.state.labsDictLoading = action.payload;
        break;
                
      // НОВЫЕ ДЕЙСТВИЯ ДЛЯ ЭКСПЕДИЦИЙ
      case 'SET_EXPEDITIONS_LIST':
        this.state.expeditionsList = action.payload;
        this.state.expeditionsLoading = false;
        this.state.expeditionsError = null;
        break;

      case 'SELECT_EXPEDITION':
        this.state.selectedExpedition = action.payload;
        break;

      case 'SET_EXPEDITIONS_LOADING':
        this.state.expeditionsLoading = action.payload;
        if (action.payload) this.state.expeditionsError = null;
        break;

      case 'SET_EXPEDITIONS_ERROR':
        this.state.expeditionsError = action.payload;
        this.state.expeditionsLoading = false;
        break;

      // НОВЫЕ CASE ДЛЯ ФЛОТА
      case 'SET_VESSELS_LIST':
        this.state.vesselsList = action.payload;
        this.state.vesselsLoading = false;
        this.state.vesselsError = null;
        break;

      case 'SELECT_VESSEL':
        this.state.selectedVessel = action.payload;
        break;


      // НОВОЕ: Обновление фотографии у выбранного судна
      case 'SET_VESSEL_PHOTO':
        if (this.state.selectedVessel && this.state.selectedVessel.id === action.payload.id) {
          this.state.selectedVessel = { 
            ...this.state.selectedVessel, 
            photo: action.payload.photo 
          };
        }
        break;
        
      case 'SET_VESSELS_LOADING':
        this.state.vesselsLoading = action.payload;
        if (action.payload) this.state.vesselsError = null;
        break;

      case 'SET_VESSELS_ERROR':
        this.state.vesselsError = action.payload;
        this.state.vesselsLoading = false;
        break;

      case 'SELECT_TRAVERSE':
        this.state.selectedTraverse = action.payload;
        this.state.selectedObservation = null;
        this.state.selectedSite = null;
        this.state.isSidebarOpen = true;
        this.state.isLoading = false;
        this.state.error = null;
        break;

      case 'SELECT_SITE':
        this.state.selectedSite = action.payload;
        this.state.selectedObservation = null;
        this.state.selectedTraverse = null;
        this.state.isSidebarOpen = true;
        this.state.isLoading = false;
        this.state.error = null;
        break;
        
      case 'SET_SITE_TIMESERIES':
        this.state.siteTimeSeries = action.payload;
        break;
      case 'SET_SITE_TIMESERIES_LOADING':
        this.state.siteTimeSeriesLoading = action.payload;
        break;
      case 'SET_SELECTED_SITE_PARAMETER':
        this.state.selectedSiteParameter = action.payload;
        break;
  
      case 'SELECT_OBSERVATION':
        this.state.selectedObservationId = action.payload.id;
        this.state.selectedObservation = action.payload;
        this.state.insituSamples = []; // Сброс при выборе новой точки
        this.state.labSamples = [];    // Сброс при выборе новой точки
        this.state.activeTab = 'info';
        this.state.isSidebarOpen = true;
        this.state.error = null;
        this.state.isLoading = false;
        break;

      case 'SET_INSITU_SAMPLES':
        this.state.insituSamples = action.payload;
        break;

      case 'SET_LAB_SAMPLES':
        this.state.labSamples = action.payload;
        break;

      case 'SET_ACTIVE_TAB':
        this.state.activeTab = action.payload;
        break;

      case 'SET_LOADING':
        this.state.isLoading = action.payload;
        if (action.payload) {
          this.state.error = null; // Сброс ошибки при начале новой загрузки
        }
        break;

      case 'SET_ERROR':
        this.state.error = action.payload;
        this.state.isLoading = false;
        break;

      case 'TOGGLE_SIDEBAR':
        this.state.isSidebarOpen = action.payload !== undefined 
          ? action.payload 
          : !this.state.isSidebarOpen;
        break;

      case 'CLEAR_SELECTION':
        this.state.selectedTraverse = null;
        this.state.selectedObservationId = null;
        this.state.selectedObservation = null;
        this.state.selectedSite = null;
        this.state.insituSamples = [];
        this.state.labSamples = [];
        this.state.error = null;
        this.state.isSidebarOpen = false;
        break;

      case 'SET_FILTER_VALUE':
        this.state.filters.values[action.payload.filterId] = action.payload.value;
        break;
      case 'SET_FILTER_OPTIONS':
        this.state.filters.options[action.payload.filterId] = action.payload.options;
        break;
      case 'SET_FILTER_LOADING':
        this.state.filters.loading[action.payload.filterId] = action.payload.loading;
        break;
      case 'RESET_FILTER':
        this.state.filters.values[action.payload] = null; //delete this.state.filters.values[action.payload];
        break;
      case 'RESET_ALL_FILTERS':
        this.state.filters.values = {};
        break;

      case 'SET_PAGES_LIST':
        this.state.pagesList = action.payload;
        break;
      case 'SET_PAGES_LOADING':
        this.state.pagesLoading = action.payload;
        break;
      case 'SET_PAGES_ERROR':
        this.state.pagesError = action.payload;
        break;
      case 'SELECT_PAGE':
        this.state.selectedPage = action.payload;
        // При выборе новой страницы сбрасываем содержимое
        this.state.pageContent = null;
        this.state.pageError = null;
        break;
      case 'SET_PAGE_CONTENT':
        this.state.pageContent = action.payload;
        break;
      case 'SET_PAGE_LOADING':
        this.state.pageLoading = action.payload;
        break;
      case 'SET_PAGE_ERROR':
        this.state.pageError = action.payload;
        break;
    }

    this.notify();
  }

  /**
   * Подписаться на изменения состояния
   * @returns функция отписки
   */
  subscribe(listener: (state: AppState) => void): () => void {
    this.listeners.add(listener);
    
    // Вызываем listener асинхронно, чтобы избежать рекурсии при первоначальной подписке
    setTimeout(() => {
      try {
        listener(this.getState());
      } catch (error) {
        console.error('Ошибка в подписчике при инициализации:', error);
      }
    }, 0);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Уведомить всех подписчиков об изменении состояния
   */
  private notify(): void {
    // Защита от рекурсивных вызовов notify
    if (this.isNotifying) {
      console.warn('Рекурсивный вызов notify предотвращён');
      return;
    }

    this.isNotifying = true;
    const currentState = this.getState();

    try {
      this.listeners.forEach(listener => {
        try {
          listener(currentState);
        } catch (error) {
          // Ловим ошибки в UI-компонентах, чтобы они не ломали всю систему состояний
          console.error('Ошибка в подписчике при обновлении состояния:', error);
        }
      });
    } finally {
      this.isNotifying = false;
    }
  }
}

export const store = new StateManager();